// 099 D08 生成健康、Token 与费用：三态分列 + 耗时分位 + 分 Agent + 成本口径不重复相加
import { useState } from "react";
import { ops } from "../../lib/api";
import { useOpsQuery } from "./useOpsQuery";
import { Bar, DaySeg, Kpi, Note, Sect, StatusTag, fmtMoney, fmtMs, fmtPct, fmtTokens } from "./widgets";

export default function GenCost() {
  const [days, setDays] = useState(7);
  const q = useOpsQuery(() => ops.genHealth(days), [days]);

  if (q.loading && !q.data) return <p className="muted">加载中…</p>;
  if (q.forbidden) return <p>🔒 {q.forbidden}</p>;
  if (q.error) return <p style={{ color: "var(--err)" }}>加载失败：{q.error}</p>;
  if (!q.data) return null;
  const d = q.data;
  const st = d.status;
  const denom = Math.max(1, st.done + st.failed + st.canceled);

  return (
    <>
      <h2 className="d-title">生成健康、Token 与费用</h2>
      <p className="d-sub2">执行 done ≠ 质量认证 ≠ 文件交付，三态分列 · 成本双口径并列不相加</p>

      <div className="d-filter">
        <DaySeg days={days} onChange={setDays} />
        <span className="d-tz">时区 {d.range.timezone}</span>
        <span className="d-updated">更新于 <b>{new Date(d.range.updatedAt).toLocaleString("zh-CN", { timeZone: "Asia/Shanghai", hour12: false })}</b></span>
      </div>

      {d.alert.over && (
        <div className="d-alert">
          ⚠ <span><b>日成本预警</b>：今日已消耗 <b>{fmtMoney(d.alert.todayCostCents)}</b> / 阈值 {fmtMoney(d.alert.thresholdCents)}。当前仅本页内提醒；外部推送见 D14（待批）。</span>
        </div>
      )}

      <Sect title={`任务状态分布（范围内发起 ${st.total} 单同一队列）`} tag="D08" />
      <div className="d-tblwrap">
        <table>
          <thead><tr><th>维度</th><th>分类</th><th className="num">数量</th><th className="num">占比</th><th></th></tr></thead>
          <tbody>
            <tr><td rowSpan={4}>执行状态</td><td>done</td><td className="num">{st.done}</td><td className="num">{fmtPct(st.done / denom)}</td><td><Bar pct={(st.done / denom) * 100} kind="g" /></td></tr>
            <tr><td>failed</td><td className="num">{st.failed}</td><td className="num">{fmtPct(st.failed / denom)}</td><td><Bar pct={(st.failed / denom) * 100} kind="r" /></td></tr>
            <tr><td>canceled</td><td className="num">{st.canceled}</td><td className="num">{fmtPct(st.canceled / denom)}</td><td><Bar pct={(st.canceled / denom) * 100} kind="y" /></td></tr>
            <tr><td>运行中（快照）</td><td className="num">{st.runningNow}</td><td className="num">—</td><td className="muted small">快照不参与比率</td></tr>
            {d.quality ? (
              <>
                <tr><td rowSpan={4}>质量认证（done 的 {d.quality.doneTotal} 单）</td><td>certified（文件认证通过）</td><td className="num">{d.quality.certified}</td><td className="num">{fmtPct(d.quality.doneTotal ? d.quality.certified / d.quality.doneTotal : null)}</td><td><Bar pct={d.quality.doneTotal ? (d.quality.certified / d.quality.doneTotal) * 100 : 0} kind="g" /></td></tr>
                <tr><td>needs_revision</td><td className="num">{d.quality.needs_revision}</td><td className="num">{fmtPct(d.quality.doneTotal ? d.quality.needs_revision / d.quality.doneTotal : null)}</td><td><Bar pct={d.quality.doneTotal ? (d.quality.needs_revision / d.quality.doneTotal) * 100 : 0} kind="y" /></td></tr>
                <tr><td>human_review</td><td className="num">{d.quality.human_review}</td><td className="num">{fmtPct(d.quality.doneTotal ? d.quality.human_review / d.quality.doneTotal : null)}</td><td><Bar pct={d.quality.doneTotal ? (d.quality.human_review / d.quality.doneTotal) * 100 : 0} /></td></tr>
                <tr><td>未审（无 quality_status）</td><td className="num">{d.quality.unreviewed}</td><td className="num">{fmtPct(d.quality.doneTotal ? d.quality.unreviewed / d.quality.doneTotal : null)}</td><td className="muted small">done ≠ 认证</td></tr>
              </>
            ) : (
              <tr><td>质量认证</td><td colSpan={4} className="muted">未采集——质量认证字段（jobs.quality_status）随 harness v2 上线后开放；当前 done 仅代表执行完成</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="cols2">
        <div>
          <Sect title="耗时与队列" />
          <div className="d-tblwrap">
            <table>
              <thead><tr><th>指标</th><th className="num">数值</th><th>说明</th></tr></thead>
              <tbody>
                <tr><td>端到端完成耗时中位</td><td className="num">{fmtMs(d.durationMs.median)}</td><td className="muted">样本 {d.durationMs.sample}</td></tr>
                <tr><td>端到端 P95</td><td className="num">{fmtMs(d.durationMs.p95)}</td><td className="muted">样本不足时不展示</td></tr>
                <tr><td>队列等待中位</td><td className="num">{fmtMs(d.queueWaitMs.median)}</td><td className="muted">入队 → 首步骤开跑 · 样本 {d.queueWaitMs.sample}</td></tr>
                <tr><td>降级执行</td><td className="num">{d.degraded}</td><td className="muted">如配图占位图</td></tr>
              </tbody>
            </table>
          </div>
          <div style={{ height: 11 }} />
          <Sect title="分 Agent 步骤健康" />
          <div className="d-tblwrap">
            <table>
              <thead><tr><th>Agent</th><th className="num">运行</th><th className="num">失败</th><th className="num">重试</th></tr></thead>
              <tbody>
                {d.agents.map((a) => (
                  <tr key={a.agent}>
                    <td>{a.agent}</td>
                    <td className="num">{a.runs}</td>
                    <td className="num" style={a.failed ? { color: "var(--err)", fontWeight: 700 } : undefined}>{a.failed}</td>
                    <td className="num">{a.retried}</td>
                  </tr>
                ))}
                {!d.agents.length && <tr><td colSpan={4} className="muted">范围内无步骤数据</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <Sect title="Token 与费用（估算）" />
          <div className="d-tblwrap">
            <table>
              <thead><tr><th>模型</th><th className="num">调用</th><th className="num">输入 Token</th><th className="num">输出 Token</th><th className="num">估算费用</th><th>占比</th></tr></thead>
              <tbody>
                {d.models.map((m) => (
                  <tr key={m.model}>
                    <td>{m.model}</td>
                    <td className="num">{m.calls}</td>
                    <td className="num">{fmtTokens(m.tokensIn)}</td>
                    <td className="num">{fmtTokens(m.tokensOut)}</td>
                    <td className="num">{fmtMoney(m.costCents)}</td>
                    <td><Bar pct={d.costCrossCheck.llmCallsCostCents ? (m.costCents / d.costCrossCheck.llmCallsCostCents) * 100 : 0} /></td>
                  </tr>
                ))}
                <tr>
                  <td>配图（按张计费）</td><td className="num">{d.image.count}</td><td className="num muted">—</td><td className="num">{d.image.count ? `${d.image.count} 张` : "—"}</td>
                  <td className="num">{fmtMoney(d.image.costCents)}</td>
                  <td><span className="tag t-yellow">单列 · 估算</span></td>
                </tr>
                <tr>
                  <td><b>合计（llm_calls 台账）</b></td><td className="num"><b>{d.models.reduce((a, m) => a + m.calls, 0) + d.image.count}</b></td>
                  <td className="num"><b>{fmtTokens(d.models.reduce((a, m) => a + m.tokensIn, 0))}</b></td>
                  <td className="num"><b>{fmtTokens(d.models.reduce((a, m) => a + m.tokensOut, 0))}</b></td>
                  <td className="num"><b>{fmtMoney(d.costCrossCheck.llmCallsCostCents)}</b></td><td></td>
                </tr>
              </tbody>
            </table>
          </div>
          <Note>
            口径（099 §4）：成本以 llm_calls 调用台账为权威（含失败/重试/取消单），jobs 归属累计 {fmtMoney(d.costCrossCheck.jobsCostCents)} 仅作交叉核对，两者不相加。
            失败与重试消耗计入；无厂商账单接口，不显示「真实剩余额度」。
          </Note>
        </div>
      </div>

      <Sect title="重新生成率（质量代理指标）" tag="评审补充" hint="同一项目反复重来 = 不满意信号" />
      <div className="d-grid d-kpis" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))" }}>
        <Kpi label="多轮生成项目占比" value={d.regeneration.multiRoundRate !== null ? fmtPct(d.regeneration.multiRoundRate) : "—"} delta={`${d.regeneration.multiRound}/${d.regeneration.projectsWithJobs} 个有生成的项目 > 1 轮`} />
        <Kpi label="人均生成轮次" value={d.regeneration.avgRounds !== null ? d.regeneration.avgRounds.toFixed(2) : "—"} delta={`jobs / 有生成的项目`} />
        <Kpi label="口径说明" value="不抬高" delta={d.regeneration.note} />
      </div>

      <Sect title="高消耗任务 Top 5" />
      <div className="d-tblwrap">
        <table>
          <thead><tr><th>任务</th><th>项目</th><th className="num">Token</th><th className="num">估算成本</th><th>状态</th></tr></thead>
          <tbody>
            {d.topJobs.map((j) => (
              <tr key={j.id}>
                <td className="num">{j.id.slice(0, 8)}</td>
                <td>{j.projectName ?? j.projectId.slice(0, 8)}</td>
                <td className="num">{fmtTokens(j.tokensTotal)}</td>
                <td className="num">{fmtMoney(j.costCents)}</td>
                <td><StatusTag status={j.status} quality={j.qualityStatus} /></td>
              </tr>
            ))}
            {!d.topJobs.length && <tr><td colSpan={5} className="muted">范围内无任务</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}
